import { Box, Card, CardContent, Skeleton, Stack, Typography, IconButton, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Alert, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState } from "react";
import { PatientDetailScreenQuery } from "~/graphql/__generated__/graphql";
import { startCase } from "lodash/fp";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { gql } from "@apollo/client";
import { PatientDetailScreenDocument, useDeletePatientCareTeamMutation } from "~/graphql/__generated__/graphql";
import CareTeamEditDialog from "./CareTeamEditDialog";

type PatientPreferredProvidersProps = {
  continuityProviders?: (NonNullable<PatientDetailScreenQuery["patientSingle"]>['careTeamProviders'][number] & {lastVisit?: {visitType?: {name: string} | null, startTime: string}})[];
  loading: boolean;
  patientId: number;
  patientName: string;
}

gql`
  mutation DeletePatientCareTeam($patientId: Int!, $providerTypeId: Int!) @permission(requires: useWebApp) {
    deletePatientCareTeam(patientId: $patientId, providerTypeId: $providerTypeId)
  }
`;

const PatientCareTeamList: React.FC<PatientPreferredProvidersProps> = ({ 
  continuityProviders, 
  loading, 
  patientId, 
  patientName
}) => {
  const { providerTypes } = useOrgConfig();
  const { datef } = useZonedDateFormat();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<{providerTypeId: number, providerId: number} | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<{providerTypeId: number, providerName: string} | null>(null);
  const [deleteCareTeam, { loading: deleteLoading }] = useDeletePatientCareTeamMutation();
  
  const groupedProviders = continuityProviders?.reduce((groups: Record<string, (typeof continuityProviders)[number][]>, provider: (typeof continuityProviders)[number]) => {
    if (!provider.provider?.providerTypeId) return groups;
    const providerTypeName = providerTypes[provider.provider?.providerTypeId]?.name || 'Unknown Provider Type';
    if (!groups[providerTypeName]) {
      groups[providerTypeName] = [];
    }
    groups[providerTypeName].push(provider);
    return groups;
  }, {} as Record<string, (typeof continuityProviders)[number][]>) ?? {};

  const handleDeleteCareTeamMember = (providerTypeId: number, providerName: string) => {
    setMemberToDelete({ providerTypeId, providerName });
    setShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      setIsProcessing(true);
      await deleteCareTeam({
        variables: { patientId, providerTypeId: memberToDelete.providerTypeId },
        refetchQueries: [
          {
            query: PatientDetailScreenDocument,
            variables: { patientId },
          },
        ],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      console.error('Failed to delete care team member:', error);
    } finally {
      setIsProcessing(false);
      setShowDeleteConfirmation(false);
      setMemberToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
    setMemberToDelete(null);
  };

  const handleEditMember = (providerTypeId: number, providerId: number) => {
    setEditingMember({ providerTypeId, providerId });
    setShowEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingMember(null);
  };

  const theme = useTheme();

  return (
    <Grid container style={{ marginTop: 30, marginBottom: 14 }}>
      <Grid size={12}>
        <Card sx={{ minWidth: "100%" }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography fontWeight={500} component="span">
                Care Team
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={() => setShowEditDialog(true)}
                disabled={loading || deleteLoading || isProcessing}
              >
                Add Member
              </Button>
            </Stack>
            {(loading || isProcessing) && (
              <Stack spacing={2}>
                <Skeleton variant="text" sx={{ fontSize: "1rem" }} width={240} />
                <Skeleton variant="text" sx={{ fontSize: "0.8rem" }} width={180} />
                <Skeleton variant="text" sx={{ fontSize: "0.8rem" }} width={200} />
              </Stack>
            )}
            
            {!loading && !isProcessing && continuityProviders && continuityProviders.length > 0 ? (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 2,
                  gridAutoRows: '1fr'
                }}>
                  {Object.entries(groupedProviders).map(([providerTypeName, providers]) => (
                    <Box key={providerTypeName} sx={{
                      border: `1px solid ${theme.palette.primary}`,
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      <Typography variant="subtitle1" sx={{ 
                        mb: 1.5, 
                        color: '#1976d2',
                        fontWeight: 600,
                        fontSize: '1rem',
                        borderBottom: '1px solid #e0e0e0',
                        pb: 0.5
                      }}>
                        {startCase(providerTypeName)}
                      </Typography>
                      
                      <Stack spacing={1} sx={{ flex: 1 }}>
                        {providers.map((careTeamProviderData, index) => (
                          <Box key={`${careTeamProviderData.id}-${careTeamProviderData.provider?.id}-${index}`}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.25, fontSize: '0.9rem' }}>
                                  {careTeamProviderData.provider?.firstName} {careTeamProviderData.provider?.lastName}
                                </Typography>
                                
                                <Typography variant="caption" color="text.secondary" sx={{ 
                                  fontSize: '0.75rem',
                                  fontStyle: 'italic',
                                  display: 'block'
                                }}>
                                {careTeamProviderData.lastVisit &&
                                  `Last seen ${`${datef(careTeamProviderData.lastVisit.startTime, "MMM d yyyy")}`} ${careTeamProviderData.lastVisit.visitType?.name || ''}`}
                                </Typography>
                              </Box>
                              
                              <Stack direction="row" sx={{ ml: 1 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditMember(careTeamProviderData.providerTypeId, careTeamProviderData.providerId)}
                                  disabled={deleteLoading || isProcessing}
                                  sx={{ 
                                    '&:hover': { color: 'primary.main' },
                                    p: 0.5
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteCareTeamMember(
                                    careTeamProviderData.providerTypeId,
                                    `${careTeamProviderData.provider?.firstName} ${careTeamProviderData.provider?.lastName}`
                                  )}
                                  disabled={deleteLoading || isProcessing}
                                  sx={{ 
                                    '&:hover': { color: 'error.main' },
                                    p: 0.5
                                  }}
                                >
                                  {(deleteLoading || isProcessing) ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Stack>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : !loading && !isProcessing && (
              <Typography variant="body2" color="text.secondary">
                No care team assigned
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      <CareTeamEditDialog
        open={showEditDialog}
        onClose={handleCloseEditDialog}
        patientId={patientId}
        patientName={patientName}
        existingCareTeam={continuityProviders || []}
        editingMember={editingMember}
      />
      
      {/* Delete Confirmation Modal */}
      <Dialog 
        open={showDeleteConfirmation} 
        onClose={cancelDelete} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Remove Care Team Member</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Are you sure you want to remove <strong>{memberToDelete?.providerName}</strong> from {patientName}'s care team?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={isProcessing}
            startIcon={isProcessing ? <CircularProgress size={20} /> : null}
          >
            {isProcessing ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PatientCareTeamList; 
